import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

function CategoryModal({ category, categories, onClose, onSaved }: { category?: any; categories: any[]; onClose: () => void; onSaved: () => void; }) {
  const isEdit = !!category;
  const [form, setForm] = useState({ name: category?.name || '', description: category?.description || '', parent_id: category?.parent_id || '', sort_order: category?.sort_order || 0, is_active: category?.is_active !== false, show_in_menu: category?.show_in_menu !== false });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) { toast.error('Name is required'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, typeof v === 'boolean' ? (v ? '1' : '0') : String(v)));
      if (imageFile) formData.append('image', imageFile);
      if (isEdit) {
        formData.append('_method', 'PUT');
        await api.post(`/admin/categories/${category.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Category updated!');
      } else {
        await api.post('/admin/categories', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Category created!');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (errors) {
        (Object.values(errors) as string[][]).flat().forEach(msg => toast.error(msg));
      } else {
        toast.error(err.response?.data?.message || 'Failed to save');
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Category' : 'Add Category'}</h2>
          <button onClick={onClose}><X size={20} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Category Name *</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" placeholder="e.g., Men, Women, Summer" />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Parent Category</label>
            <select value={form.parent_id} onChange={e => setForm(p => ({ ...p, parent_id: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 bg-white">
              <option value="">None (Top Level)</option>
              {categories.filter(c => c.id !== category?.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 resize-none" />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Sort Order</label>
            <input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: Number(e.target.value) }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-2">Category Image</label>
            <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files?.[0] || null)} className="text-sm text-gray-600" />
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="w-4 h-4" />
              <span className="text-sm text-gray-700">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.show_in_menu} onChange={e => setForm(p => ({ ...p, show_in_menu: e.target.checked }))} className="w-4 h-4" />
              <span className="text-sm text-gray-700">Show in Menu</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-6 py-2.5 border border-gray-300 text-sm font-medium hover:border-gray-900 transition-colors">Cancel</button>
            <button type="submit" disabled={loading} className="px-8 py-2.5 bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors disabled:opacity-60">
              {isEdit ? 'Save Changes' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminCategories() {
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<any>(null);

  const fetchCategories = () => {
    setLoading(true);
    api.get('/admin/categories').then(r => setCategories(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch { toast.error('Cannot delete — may have products'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
        <button onClick={() => { setEditCategory(null); setModalOpen(true); }} className="flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-gray-700 transition-colors">
          <Plus size={16} /> Add Category
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Parent</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Children</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sort</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Menu</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? Array(5).fill(null).map((_, i) => (
              <tr key={i}><td colSpan={7} className="px-6 py-4"><div className="h-8 bg-gray-100 animate-pulse rounded" /></td></tr>
            )) : categories.map(cat => (
              <tr key={cat.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {cat.image && <img src={getImageUrl(cat.image)} alt={cat.name} className="w-10 h-10 object-cover rounded" />}
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{cat.name}</p>
                      <p className="text-xs text-gray-400">/{cat.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{cat.parent?.name || '—'}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{cat.children?.length || 0}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{cat.sort_order}</td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${cat.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{cat.is_active ? 'Active' : 'Inactive'}</span>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${cat.show_in_menu ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>{cat.show_in_menu ? 'Yes' : 'No'}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => { setEditCategory(cat); setModalOpen(true); }} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"><Edit size={15} /></button>
                    <button onClick={() => handleDelete(cat.id, cat.name)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <CategoryModal
          category={editCategory}
          categories={categories}
          onClose={() => { setModalOpen(false); setEditCategory(null); }}
          onSaved={fetchCategories}
        />
      )}
    </div>
  );
}
